/**
 * [API 클라이언트] - 백엔드 REST API 호출 래퍼
 *
 * - Authorization Bearer 토큰 자동 첨부
 * - 401/403 응답 시 토큰 갱신 후 요청 재시도 (1회)
 * - 갱신 실패 시 토큰 삭제 및 로그인 페이지로 리다이렉트
 * - FormData(파일 업로드) / JSON 본문 자동 처리
 * - credentials: 'include'로 HttpOnly 쿠키 전송
 */
import {
  getAccessToken,
  getGhostControlToken,
  setGhostControlToken,
  refreshAccessTokenDetailed,
  scheduleRefreshRetry,
  clearTokens,
  clearRefreshCookie,
  redirectToLogin,
} from './auth';
import { isCredentialPreservingContext } from './liveMediaSession';
import type { ApiErrorResponse } from './types';

export class ApiError extends Error {
  public fieldErrors?: Array<{ field: string; message: string }>;
  public data?: Record<string, unknown>;

  constructor(
    public code: string,
    message: string,
    public status?: number,
    fieldErrors?: Array<{ field: string; message: string }>,
    data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.fieldErrors = fieldErrors;
    this.data = data;
  }
}

interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown> | FormData | null;
  /** 'json'(기본값)이면 JSON 파싱, 'blob'이면 Blob 반환 */
  responseType?: 'json' | 'blob';
  authRecovery?: 'default' | 'none';
  onResponseStatus?: (status: number) => void;
}

// 갱신 요청 중복 방지는 auth.ts의 refreshAccessToken()에서 처리한다.
// 모든 호출자(apiFetch 401, 타이머, 초기화, WebSocket)가 단일 Promise를 공유한다.

/**
 * API 호출 범용 fetch 래퍼.
 *
 * - Authorization Bearer 토큰 자동 첨부
 * - 401 응답 시: /api/auth/refresh로 토큰 갱신 후 원래 요청 1회 재시도
 * - 갱신 실패 시: 토큰 삭제 및 로그인 페이지 리다이렉트
 * - 실패 시 ApiError(code, message) 예외 발생
 * - credentials: 'include'로 쿠키 전송
 * - 기본 Content-Type: application/json (FormData는 자동 제외)
 */
export async function apiFetch<T>(endpoint: string, options: ApiFetchOptions = {}): Promise<T> {
  const responseType = options.responseType ?? 'json';
  const ghostCredential = /^\/api\/meetings\/\d+(?:\/|$)/.test(endpoint)
    ? (new Headers(options.headers).get('X-Ghost-Control-Token') ??
      (!getAccessToken() ? getGhostControlToken() : null))
    : null;
  const response = await executeRequest(endpoint, options);
  options.onResponseStatus?.(response.status);
  if (options.authRecovery === 'none') return handleResponse<T>(response, responseType);

  // A scoped Ghost credential cannot be refreshed with an account login cookie.
  // A target/action 403 may still belong to a valid Ghost; only 401 invalidates the credential.
  if (ghostCredential && (response.status === 401 || response.status === 403)) {
    if (response.status === 401 && getGhostControlToken() === ghostCredential)
      setGhostControlToken(null);
    return handleResponse<T>(response, responseType);
  }

  // 401 처리 — 토큰 갱신 후 1회 재시도. 인증 엔드포인트(로그인/로그아웃 등)는 제외.
  if (response.status === 401 && !endpoint.startsWith('/api/auth/')) {
    const outcome = await refreshAccessTokenDetailed();

    if (outcome.ok) {
      // 새 토큰으로 원래 요청 재시도
      const retryResponse = await executeRequest(endpoint, options);
      return handleResponse<T>(retryResponse, responseType);
    }

    return handleRefreshFailure<T>(outcome.kind, response.status);
  }

  // 403 처리 — 토큰이 만료/분실되어 전송되지 않은 경우에만 갱신 시도.
  // 실제 권한 부족(토큰 있음)인 경우는 갱신하지 않는다.
  if (response.status === 403 && !endpoint.startsWith('/api/auth/') && !getAccessToken()) {
    const outcome = await refreshAccessTokenDetailed();

    if (outcome.ok) {
      const retryResponse = await executeRequest(endpoint, options);
      return handleResponse<T>(retryResponse, responseType);
    }

    return handleRefreshFailure<T>(outcome.kind, response.status);
  }

  return handleResponse<T>(response, responseType);
}

/**
 * 리프레시 실패 후처리 — 401/403 공통.
 *
 * - transient AND 라이브(자격증명 보존) 컨텍스트: 로그아웃/리다이렉트를 전부 생략하고
 *   백오프 재시도만 예약한 뒤 AUTH_REFRESH_TRANSIENT 로 throw(회의 세션 보존 — P1-B 핵심).
 * - definitive 또는 비라이브: 기존과 **바이트 동일**(토큰 삭제 + 쿠키삭제 + 로그인 리다이렉트 + TOKEN_EXPIRED).
 */
async function handleRefreshFailure<T>(
  kind: 'transient' | 'definitive',
  status: number,
): Promise<T> {
  if (
    kind === 'transient' &&
    typeof window !== 'undefined' &&
    isCredentialPreservingContext(window.location.pathname)
  ) {
    scheduleRefreshRetry();
    throw new ApiError(
      'AUTH_REFRESH_TRANSIENT',
      '연결이 불안정합니다. 자동으로 다시 시도합니다.',
      status,
    );
  }

  clearTokens();
  await clearRefreshCookie();
  redirectToLogin();
  throw new ApiError('TOKEN_EXPIRED', 'Session expired. Please log in again.', 401);
}

/** 적절한 헤더와 인증 정보를 포함하여 실제 fetch 요청을 실행한다. */
async function executeRequest(endpoint: string, options: ApiFetchOptions): Promise<Response> {
  const {
    body,
    headers: customHeaders,
    responseType: _rt,
    authRecovery: _authRecovery,
    onResponseStatus: _onResponseStatus,
    ...restOptions
  } = options;

  const headers = new Headers(customHeaders);

  // 토큰이 존재하면 Authorization 헤더 추가
  const token = getAccessToken();
  const explicitGhost =
    /^\/api\/meetings\/\d+(?:\/|$)/.test(endpoint) && headers.has('X-Ghost-Control-Token');
  if (explicitGhost) {
    // Ghost 명령에 다른 로그인 계정이 섞이면 서버는 그 계정의 권한으로 판정한다.
    headers.delete('Authorization');
  } else if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    // 임베드 Ghost 전용 대체 인증 — 로그인 세션이 없을 때만 실린다.
    // 서버는 /api/meetings/{id}/** 경로에서만 이 헤더를 인정하며, 지정 해제 시 즉시 무효화된다.
    const ghost = getGhostControlToken();
    if (ghost) headers.set('X-Ghost-Control-Token', ghost);
  }

  // FormData가 아닌 경우 Content-Type 설정 (FormData는 브라우저가 자동 설정)
  const isFormData = body instanceof FormData;
  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const fetchOptions: RequestInit = {
    ...restOptions,
    headers,
    credentials: 'include',
  };

  // 요청 본문 직렬화
  if (body !== undefined && body !== null) {
    if (isFormData) {
      fetchOptions.body = body;
    } else {
      fetchOptions.body = JSON.stringify(body);
    }
  }

  // 모든 요청은 같은 오리진(상대 URL)을 사용한다.
  // 프로덕션: nginx가 /api/*를 백엔드로 프록시
  // 로컬 개발: Next.js rewrites가 프록시 처리
  // 인증 엔드포인트(/api/auth/login 등)는 Next.js 라우트 핸들러가 가로채서
  // 리프레시 토큰의 Set-Cookie 헤더를 전달한다.
  return fetch(endpoint, fetchOptions);
}

/**
 * 응답을 파싱하고 2xx가 아닌 상태 코드에 대해 ApiError를 발생시킨다.
 *
 * (export 사유: 회귀 테스트가 구성된 Response로 오류 매핑을 직접 검증하는 테스트 시임.)
 */
export async function handleResponse<T>(
  response: Response,
  responseType: 'json' | 'blob' = 'json',
): Promise<T> {
  if (response.ok) {
    // 204 No Content 처리
    if (response.status === 204) {
      return undefined as T;
    }

    // Blob 응답 — 바이너리 데이터 그대로 반환
    if (responseType === 'blob') {
      return response.blob() as Promise<T>;
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
      return response.json() as Promise<T>;
    }

    return undefined as T;
  }

  // 에러 응답 본문 파싱
  let errorData:
    | (ApiErrorResponse & { fieldErrors?: Array<{ field: string; message: string }> })
    | null = null;
  try {
    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    }
  } catch {
    // 에러 본문 파싱 실패 — 기본 에러 메시지 사용
  }

  // 1) 백엔드/프록시가 JSON 오류 메시지를 준 경우 — 기존 우선순위를 그대로 보존한다.
  //    (비즈니스 오류 메시지는 이미 한국어이며 다른 소비처가 그대로 토스트로 쓴다.)
  if (errorData?.message) {
    throw new ApiError(
      errorData.code || 'UNKNOWN_ERROR',
      errorData.message,
      response.status,
      errorData.fieldErrors,
      errorData.data,
    );
  }

  // 2) 비JSON(또는 message 없는) 오류 — 사용자에겐 영어 원문 대신 한국어 안내를 노출한다.
  //    운영 nginx가 백엔드(8081)에서 유효 응답을 못 받으면 text/html 502/503/504를 낸다.
  //    이 경우 errorData 가 없어 종전엔 "Request failed with status 502" 원문이 노출됐다.
  if (response.status === 502 || response.status === 503 || response.status === 504) {
    throw new ApiError(
      'UPSTREAM_UNAVAILABLE',
      '서버와 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
      response.status,
      errorData?.fieldErrors,
      errorData?.data,
    );
  }

  // 3) 그 외 비JSON 오류 — 상태코드는 디버깅용으로 보존하되 문구는 한국어로 대체한다.
  throw new ApiError(
    errorData?.code || 'UNKNOWN_ERROR',
    `요청 처리에 실패했습니다 (오류 코드: ${response.status})`,
    response.status,
    errorData?.fieldErrors,
    errorData?.data,
  );
}
